import { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ListSkeleton } from '@/components/ui/skeleton';
import { useSalesOrderStore } from '../store/salesOrderStore';
import type { FirmOfferSummaryDto } from '../types/salesOrder.types';

interface Props {
  selectedTranId: string | null;
  // Emits the full summary (not just the id) so the consumer can render an
  // instant header from the list-row data while the FO detail loads.
  onSelect: (fo: FirmOfferSummaryDto) => void;
  disabled?: boolean;
}

// Show 10 most recent valid FOs when the search is empty; up to 20 when the
// user types a query. Keeps the default view scannable without forcing a
// search.
const RECENT_TAKE = 10;
const SEARCH_TAKE = 20;

// FO-SOI-3: searchable picker for awarded Firm Offers with positive balance.
// Calls /api/sales-orders/firm-offers?q=&customerId=. Debounced ~250ms.
export function FirmOfferPicker({ selectedTranId, onSelect, disabled }: Props) {
  const [query, setQuery] = useState('');
  const results = useSalesOrderStore((s) => s.foSearchResults);
  const isSearching = useSalesOrderStore((s) => s.isSearchingFo);
  const searchFirmOffers = useSalesOrderStore((s) => s.searchFirmOffers);

  // Debounce: search after 250ms of inactivity. Empty query → recent FOs.
  useEffect(() => {
    const trimmed = query.trim();
    const handle = setTimeout(() => {
      searchFirmOffers(trimmed || null, null, trimmed ? SEARCH_TAKE : RECENT_TAKE);
    }, 250);
    return () => clearTimeout(handle);
  }, [query, searchFirmOffers]);

  function handlePick(fo: FirmOfferSummaryDto) {
    setQuery('');
    onSelect(fo);
  }

  // Hide the result list once an FO is selected unless the user starts
  // typing again — keeps the right pane uncluttered after a pick.
  const showResults = !selectedTranId || query.trim().length > 0;
  const hasQuery = query.trim().length > 0;

  return (
    <div className="space-y-1.5">
      <Label htmlFor="fo-picker-search" className="text-xs font-medium text-muted-foreground">
        Select a firm offer
      </Label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id="fo-picker-search"
          placeholder={selectedTranId
            ? `Selected: ${selectedTranId} — search to change`
            : 'Search by FO# / project / customer…'}
          className="h-9 pl-9 pr-9"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
        />
        {/* Split positioning (outer span) from the rotation animation (inner
            Loader2): `animate-spin` overrides any other transform on the same
            element, which is why a spinner with both `-translate-y-1/2` and
            `animate-spin` ends up off-center AND appearing not to rotate. */}
        {isSearching && (
          <span
            aria-hidden
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
          >
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </span>
        )}
      </div>
      {showResults && (
        <div className="rounded-md border bg-card shadow-sm">
          <div className="border-b bg-muted/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {hasQuery ? 'Results' : 'Recent Firm Offers'}
          </div>
          {isSearching && results.length === 0 ? (
            <ListSkeleton rows={5} className="p-3" />
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-muted-foreground">
              {hasQuery
                ? 'No eligible Firm Offers match your search. Only awarded FOs with a positive balance are listed.'
                : 'No eligible Firm Offers available.'}
            </div>
          ) : (
            <div className="divide-y">
              {results.map((fo) => (
                <FirmOfferResultRow key={fo.internalId} fo={fo} onSelect={handlePick} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FirmOfferResultRow({
  fo,
  onSelect,
}: { fo: FirmOfferSummaryDto; onSelect: (fo: FirmOfferSummaryDto) => void }) {
  return (
    <button
      type="button"
      className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left hover:bg-muted/50"
      onClick={() => onSelect(fo)}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate font-mono text-xs font-semibold">{fo.tranId}</div>
        <div className="truncate text-[11px] text-muted-foreground">
          {fo.customerName}
          {fo.projectName ? ` · ${fo.projectName}` : ''}
        </div>
      </div>
      <div className="shrink-0 whitespace-nowrap text-right text-[11px] tabular-nums">
        <div className="font-semibold text-success">{fo.balanceKg.toFixed(2)} kg</div>
        <div className="text-muted-foreground">of {fo.totalAuthorizedKg.toFixed(2)}</div>
      </div>
    </button>
  );
}
